import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import Hero from "./components/hero";
import Header from "./components/header";
import ActivityGallery from './components/activity-gallery';
import Features from "./components/features";
import TeaserGallery from "./components/Teasergallery";
import FinalCTA from "./components/FinalCTA";
import Booking from "./components/booking";
import Footer from "./components/footer";
import Gallery from "./components/gallery";
import Overview from "./components/overview";
import Location from "./components/locationn";
import Restauration from "./components/restauration";
import Activities from "./components/activities";
import RestaurationFull from "./components/restaurationFull";
import GetHere from "./components/GetHere";
import FAQ from "./components/FAQ";
import Admin from "./components/Admin";
import About from "./components/About";
import Transfer from "./components/Transfer";
import Testimonials from "./components/Testimonials";
import Intro from "./components/Intro";
import SEO from "./components/SEO";
import { initAnalytics, trackEvent } from "./lib/analytics";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import "./components/glow.css";
import "./i18n";

// Obscure admin route (not linked anywhere, marked noindex). Override via REACT_APP_ADMIN_PATH.
const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH || "lr-staff-7c3f9a";

const villaSchema = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  name: "Laurier Rose",
  alternateName: "Laurier Rose Hostel",
  description:
    "Luxury villa retreat in the Ourika Valley at the foot of the Atlas Mountains. Offering panoramic mountain views, authentic Berber hospitality, a pool, and curated experiences.",
  url: "https://www.laurierroseourika.com",
  logo: "https://www.laurierroseourika.com/logo512.png",
  telephone: "+212661919669",
  email: "laurierroseourika@gmail.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "77XG+WV7 Taljerft",
    addressLocality: "Ourika",
    postalCode: "42452",
    addressCountry: "MA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 31.300988,
    longitude: -7.724062,
  },
  image: [
    "https://www.laurierroseourika.com/images/gallery/hero11.jpg",
    "https://www.laurierroseourika.com/images/gallery/herov.jpg",
    "https://www.laurierroseourika.com/images/gallery/hero33.jpg",
  ],
  priceRange: "MAD 500–1000",
  currenciesAccepted: "MAD",
  paymentAccepted: "Cash, Credit Card",
  availableLanguage: ["English", "French", "German", "Arabic"],
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "Free WiFi", value: true },
    { "@type": "LocationFeatureSpecification", name: "Swimming Pool", value: true },
    { "@type": "LocationFeatureSpecification", name: "Air Conditioning", value: true },
    { "@type": "LocationFeatureSpecification", name: "Free Parking", value: true },
    { "@type": "LocationFeatureSpecification", name: "Restaurant", value: true },
    { "@type": "LocationFeatureSpecification", name: "Garden", value: true },
    { "@type": "LocationFeatureSpecification", name: "Terrace", value: true },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "22",
    bestRating: "5",
  },
  checkinTime: "14:00",
  checkoutTime: "12:00",
  numberOfRooms: 6,
};

function PageViews() {
  const location = useLocation();
  useEffect(() => {
    trackEvent("page_view", {
      page_path: location.pathname + location.search,
    });
  }, [location]);
  return null;
}

function App() {
  const [showIntro, setShowIntro] = useState(() => {
    try {
      return !sessionStorage.getItem("lr_intro");
    } catch (e) {
      return true;
    }
  });
  const finishIntro = () => {
    try {
      sessionStorage.setItem("lr_intro", "1");
    } catch (e) {}
    setShowIntro(false);
  };

  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Router>
      <AnimatePresence>
        {showIntro && <Intro key="intro" onDone={finishIntro} />}
      </AnimatePresence>
      <PageViews />
      <Header />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <SEO
                title="Luxury Villa Retreat in the Atlas Mountains"
                description="Escape to Laurier Rose — a stunning luxury villa in Ourika Valley, Morocco. Pool, mountain views, authentic Berber hospitality, and unforgettable experiences from MAD 500/night."
                path="/"
                schema={villaSchema}
              />
              <Hero />
              <Features />
              <TeaserGallery />
              <Restauration />
              <Activities />
              <Testimonials />
              <FinalCTA />
              <Location />
              <GetHere />
              <FAQ />
              <Footer />
            </>
          }
        />
        <Route
          path="/gallery"
          element={
            <>
              <SEO
                title="Villa Photo Gallery"
                description="Browse stunning photos of Laurier Rose villa — exterior views, bedrooms, living areas, bathrooms, and the sauna. A luxury retreat in Ourika Valley, Morocco."
                image="https://www.laurierroseourika.com/images/gallery/exterior1.jpg"
                path="/gallery"
              />
              <Gallery />
            </>
          }
        />
        <Route
          path="/booking"
          element={
            <>
              <SEO
                title="Book Your Stay"
                description="Reserve your room at Laurier Rose villa in Ourika Valley. Choose from 6 unique rooms with Atlas Mountain views, starting from MAD 500/night. Easy online booking."
                path="/booking"
              />
              <Booking />
            </>
          }
        />
        <Route
          path="/restauration-full"
          element={
            <>
              <SEO
                title="Restaurant & Menu"
                description="Discover our authentic Moroccan menu at Laurier Rose — traditional tajines, Moroccan breakfast, mint tea, and seasonal fruits. Fresh local ingredients daily."
                image="https://www.laurierroseourika.com/images/gallery/tajine.jpg"
                path="/restauration-full"
              />
              <RestaurationFull />
            </>
          }
        />
        <Route
          path="/overview"
          element={
            <>
              <SEO
                title="Villa Overview & Amenities"
                description="Everything you need to know about Laurier Rose villa — 6 rooms, pool, free parking, breakfast, pets allowed, sauna, garden, and terrace. Ideal for families and groups."
                path="/overview"
              />
              <Overview />
            </>
          }
        />
        <Route path={`/${ADMIN_PATH}`} element={<Admin />} />
        <Route
          path="/about"
          element={
            <>
              <SEO
                title="About Us"
                description="Discover the story of Laurier Rose — a family-run luxury villa in the Ourika Valley at the foot of the Atlas Mountains. Follow our journey on Instagram."
                path="/about"
              />
              <About />
            </>
          }
        />
        <Route
          path="/transfer"
          element={
            <>
              <SEO
                title="Airport Transfer"
                description="Private door-to-door transfer between Marrakech Menara Airport and Laurier Rose villa in the Ourika Valley. Share your flight details — price on request."
                path="/transfer"
              />
              <Transfer />
            </>
          }
        />
        <Route
          path="/activity-gallery"
          element={
            <>
              <SEO
                title="Activities & Experiences"
                description="Explore activities at Laurier Rose — waterfall hikes, quad biking, mountain trails, and cultural experiences in the Ourika Valley and Atlas Mountains."
                image="https://www.laurierroseourika.com/images/gallery/quad.jpg"
                path="/activity-gallery"
              />
              <ActivityGallery />
            </>
          }
        />
      </Routes>

      <SpeedInsights />
      <Analytics />
    </Router>
  );
}

export default App;
